<template>
  <div :class="['relative flex min-h-0 flex-col', viewportClass]">
    <VueLenis
      ref="lenisRef"
      :props="{
        class: [
          'raw-panel-scroll',
          'mx-[calc(var(--raw-panel-space)*-1)] min-h-0 flex-1 overflow-y-auto px-[--raw-panel-space] [mask-image:linear-gradient(to_bottom,transparent_0,black_var(--fade-top),black_calc(100%_-_var(--fade-bottom)),transparent_100%)]',
        ],
        style: {
          '--fade-top': fadeTop,
          '--fade-bottom': fadeBottom,
          clipPath,
        },
      }"
    >
      <!-- Inner padding (not container padding) keeps resting content off the
                 edges without pushing the sticky header down from the viewport top. -->
      <div class="pb-[--raw-scroll-space-bottom] pt-[--raw-scroll-space-top]">
        <slot />
      </div>
    </VueLenis>

    <!-- Decorative: the position it reports is the scroll container's own. -->
    <div
      aria-hidden="true"
      data-scrollbar
      :data-visible="(scrollable && moving) || undefined"
      :style="{
        '--sb-inset': INSET_PX,
        '--sb-thumb-h': thumbHeight,
        '--sb-thumb-y': thumbOffset,
      }"
    >
      <div data-scrollbar-thumb />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'

import { VueLenis } from 'lenis/vue'

import 'lenis/dist/lenis.css'

defineProps<{
  /** Extra utility classes for the scroll box (e.g. flex sizing). */
  viewportClass?: string
}>()

// The viewport pulls out by `--raw-panel-space` and pads back in by the same amount:
// layout-neutral for the content, but it moves the scroll clip boundary to the host's glass
// edge, so a child can bleed to it with the same pair (`DraggableRail`). Taking the gutter
// from the token rather than repeating a number is what lets a host pad differently — the
// wide reader pads 48 — without the bleed falling short.
//
// The block space is a second, independent pair (`--raw-scroll-space-top` / `-bottom`) and
// sits on the inner content div, never on the host's card: a padding on the card's box is
// present for the whole scroll, where one on the content only shows once the scroll rests at
// its end. Two values, because a host after a symmetric box wants a wide bottom and a narrow
// lead-in under its header. Tokens for the same reason as the gutter — the host is several
// components above this one, so there is no prop to thread.
//
// The root is a positioning box with the viewport inside it, so `viewportClass` lands on
// the root and the scrollbar can sit outside the viewport's `mask-image` and `clip-path` —
// which would otherwise fade its ends and clip it.
//
// (Comments live in this block, never beside the template's root element: a second
// root-level node makes the render root a fragment, which quietly breaks an enclosing
// `<Transition>` — see `impacts/article/ArticleView.vue`.)

// Non-root Lenis: smooths scrolling inside this viewport only, leaving the map
// behind the panels fully interactive. `VueLenis` exposes its underlying Lenis
// instance through the template ref.
const lenisRef = ref<InstanceType<typeof VueLenis>>()

// The viewport's fade mask reads these so each edge only fades when it isn't
// resting at that bound — and the top fade is also suppressed while a sticky
// header is pinned, since the container mask would otherwise make that opaque
// header see-through. `0px` collapses an edge's gradient to a hard stop.
const FADE_SIZE = '8px'
const fadeTop = ref('0px')
const fadeBottom = ref('0px')

// Rounded *top* corners for the viewport, applied as a `clip-path` rather than
// `border-radius`. The viewport bleeds to the panel edge, so a radius would round there —
// but we need the corners at the *content* edge so a pinned sticky header rounds against
// the glass. The sides then run straight down to the panel edge and the bottom is left
// unclipped, so a full-bleed child (the RelatedArticles / RelatedMetals rail) bleeds edge
// to edge along the sides *and* off the bottom without being clipped. `path()` is px-based,
// so it's recomputed from the viewport box on resize; `none` until measured (and on SSR).
//
// The inset is *measured*, not a constant: the viewport's own inline padding **is** the
// host's gutter, so reading it back keeps the corners on the content edge at any padding,
// in any unit.
const CLIP_RADIUS = 16
const clipPath = ref('none')

// A self-drawn 4px bar in the host's gutter, since the native one isn't styleable across
// browsers. Indicator only: it reports the position, it never takes a pointer.
/** Shortest the thumb may get, so a very long panel still leaves a readable mark. */
const MIN_THUMB = 24
/** Keeps the bar clear of the host card's 24px corner radius at both ends. */
const TRACK_INSET = 12
/** How long the bar stays up after the last movement. */
const SCROLLBAR_HIDE_MS = 1000
const INSET_PX = `${TRACK_INSET}px`
/** The bar needs both: something to report, and recent movement to report it for. */
const scrollable = ref(false)
const moving = ref(false)
const thumbHeight = ref('0px')
const thumbOffset = ref('0px')
let hideTimer: ReturnType<typeof setTimeout> | null = null
let lastScroll = 0

function revealScrollbar() {
  moving.value = true
  if (hideTimer) clearTimeout(hideTimer)
  hideTimer = setTimeout(() => {
    moving.value = false
  }, SCROLLBAR_HIDE_MS)
}

/** Off the element, not `lenis.limit` — Lenis's is a frame stale after a content change. */
function readMetrics(wrapper: HTMLElement) {
  const view = wrapper.clientHeight
  return { view, top: wrapper.scrollTop, limit: wrapper.scrollHeight - view }
}

type Metrics = ReturnType<typeof readMetrics>

function updateClip() {
  const wrapper = lenisRef.value?.wrapper
  if (!wrapper) return
  const w = wrapper.offsetWidth
  const h = wrapper.offsetHeight
  if (!w || !h) return
  const i = parseFloat(getComputedStyle(wrapper).paddingLeft) || 0
  const r = CLIP_RADIUS
  // Arc flags `0 0 1` = minor arc, clockwise → convex corners.
  const d = [
    `M${i + r},0 L${w - i - r},0`, // top edge, inset `i` from the panel edge
    `A${r},${r} 0 0 1 ${w - i},${r}`, // top-right corner
    `L${w},${r} L${w},${h}`, // out to the panel edge, down the right side
    `L0,${h} L0,${r}`, // bottom edge (full bleed), up the left side
    `L${i},${r}`, // jog back in to the content edge
    `A${r},${r} 0 0 1 ${i + r},0 Z`, // top-left corner
  ].join(' ')
  clipPath.value = `path("${d}")`
}

function updateFades(wrapper: HTMLElement, { top, limit }: Metrics) {
  const viewportTop = wrapper.getBoundingClientRect().top
  // A header straddling the top edge owns it, so the fade must stay off or it bleeds through.
  const headerPinned = Array.from(
    wrapper.querySelectorAll<HTMLElement>('[data-sticky]'),
  ).some((header) => {
    const rect = header.getBoundingClientRect()
    return rect.top <= viewportTop + 1 && rect.bottom > viewportTop + 1
  })
  fadeTop.value = top > 1 && !headerPinned ? FADE_SIZE : '0px'
  fadeBottom.value = top < limit - 1 ? FADE_SIZE : '0px'
}

function updateScrollbar({ view, top, limit }: Metrics) {
  const track = view - TRACK_INSET * 2
  // 1px of slack, as the fades use, so a sub-pixel overflow doesn't light the bar up.
  scrollable.value = limit > 1 && track > MIN_THUMB
  if (!scrollable.value) return
  // Travel is measured against the *clamped* height, so a long panel still lands the
  // thumb on the end of the track. `top` is clamped for the overscroll bounce.
  const height = Math.max(MIN_THUMB, (track * view) / (view + limit))
  const progress = Math.min(1, Math.max(0, top / limit))
  thumbHeight.value = `${height}px`
  thumbOffset.value = `${(track - height) * progress}px`
}

/** One measurement per frame, shared: every read of the element forces layout. */
function sync() {
  const wrapper = lenisRef.value?.wrapper
  if (!wrapper) return
  const metrics = readMetrics(wrapper)
  updateFades(wrapper, metrics)
  updateScrollbar(metrics)
  // eslint-disable-next-line consistent-return
  return metrics.top
}

// Wire up once Lenis exists (the exposed `lenis` is reactive). Recompute every scroll
// frame, plus on size changes — an accordion expanding moves the limit with no scroll event.
watch(
  () => lenisRef.value?.lenis,
  (lenis, _previous, onCleanup) => {
    if (!lenis) return
    // Lenis emits `scroll` every frame its loop runs, so the bar wakes on a changed
    // position, never on the event alone — which would keep it from ever idling.
    const onScroll = () => {
      const top = sync()
      if (top === undefined || top === lastScroll) return
      lastScroll = top
      revealScrollbar()
    }
    lenis.on('scroll', onScroll)
    // A resize re-measures but never wakes the bar: content growing under you is not
    // you scrolling.
    const observer = new ResizeObserver(() => {
      sync()
      updateClip()
    })
    const { wrapper, content } = lenisRef.value ?? {}
    if (wrapper) observer.observe(wrapper)
    if (content) observer.observe(content)
    // Seeding from the first measurement keeps mounting from counting as a move.
    lastScroll = sync() ?? 0
    updateClip()
    onCleanup(() => {
      lenis.off('scroll', onScroll)
      observer.disconnect()
      if (hideTimer) clearTimeout(hideTimer)
    })
  },
  { immediate: true },
)

/** Smoothly scroll back to the top — used when a panel swaps its content. */
function scrollToTop() {
  lenisRef.value?.lenis?.scrollTo(0, { immediate: false })
}

defineExpose({ scrollToTop })
</script>

<style scoped>
.raw-panel-scroll {
  scrollbar-width: none;
}

/* Centred in whatever gutter the host pads — 6px clear of the glass at 16px, 22px at the
   reader's 48px — since the root's inline edge is the content edge. `--sb-inset` mirrors
   `TRACK_INSET`, which the thumb's travel is measured against. */
[data-scrollbar] {
  --sb-width: 4px;
  position: absolute;
  inset-block: var(--sb-inset);
  inset-inline-end: calc((var(--raw-panel-space) + var(--sb-width)) / -2);
  width: var(--sb-width);
  border-radius: 9999px;
  /*background-color: color-mix(in oklab, var(--raw-black) 8%, transparent);*/
  pointer-events: none;
  opacity: 0;
  transition: opacity var(--raw-out-quart--fast);
}

[data-scrollbar][data-visible] {
  opacity: 1;
}

/* No transition: Lenis already interpolates the scroll, so one would only add lag. */
[data-scrollbar-thumb] {
  position: absolute;
  inset-inline: 0;
  top: 0;
  height: var(--sb-thumb-h, 0px);
  border-radius: inherit;
  background-color: color-mix(in oklab, var(--raw-black) 18%, transparent);
  transform: translateY(var(--sb-thumb-y, 0px));
}

@media (prefers-reduced-motion: reduce) {
  [data-scrollbar] {
    transition-duration: 1ms;
  }
}
</style>
